/**
 * SEO helpers shared by `src/hooks/useSeo.ts`.
 *
 * One build serves many domains, so the canonical host is only knowable in the
 * browser — same constraint (and same solution) as `src/utils/pwaManifest.ts`.
 */

export const MAX_META_DESCRIPTION_LENGTH = 160

/** Resolves a path against the current origin. Absolute URLs pass through unchanged. */
export function toAbsoluteUrl(path: string): string {
  try {
    return new URL(path, window.location.origin).toString()
  } catch {
    return path
  }
}

/**
 * Turns CMS rich text into a meta-description-safe string: tags stripped,
 * entities decoded, whitespace collapsed, truncated on a word boundary.
 */
export function toMetaDescription(
  html: string | null | undefined,
  maxLength: number = MAX_META_DESCRIPTION_LENGTH,
): string | undefined {
  if (!html) return undefined

  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()

  if (!text) return undefined
  if (text.length <= maxLength) return text

  const truncated = text.slice(0, maxLength - 1)
  const lastSpace = truncated.lastIndexOf(' ')
  return `${(lastSpace > maxLength * 0.6 ? truncated.slice(0, lastSpace) : truncated).trimEnd()}…`
}
